if (!customElements.get('product-inquiry-accordion')) {
  class ProductInquiryAccordion extends HTMLElement {
    static ANIMATION_MS = 400;
    static SCROLL_OFFSET = 24;

    static KLAVIYO_API_URL = 'https://a.klaviyo.com/client/events/?company_id=XqTSUN';

    static VALIDATORS = {
      firstName: {
        test: (value) => /^[A-Za-zА-Яа-яЁё\s\-]+$/.test(value),
        empty: 'Моля, въведете име.',
        invalid: 'Името съдържа невалидни символи.',
      },
      lastName: {
        test: (value) => /^[A-Za-zА-Яа-яЁё\s\-]+$/.test(value),
        empty: 'Моля, въведете фамилия.',
        invalid: 'Фамилията съдържа невалидни символи.',
      },
      phone: {
        test: (value) => /^[0-9+\s()-]{8,20}$/.test(value),
        empty: 'Моля, въведете телефон.',
        invalid: 'Моля, въведете валиден телефон.',
      },
      email: {
        test: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
        empty: 'Моля, въведете имейл.',
        invalid: 'Моля, въведете валиден имейл.',
      },
      message: {
        test: (value) => value.length >= 10,
        empty: 'Моля, разкажете за проекта.',
        invalid: 'Съобщението трябва да е поне 10 символа.',
      },
    };

    connectedCallback() {
      this.openButton = this.querySelector('[data-product-inquiry-open]');
      this.closeButton = this.querySelector('[data-product-inquiry-close]');
      this.panel = this.querySelector('[data-product-inquiry-panel]');
      this.form = this.querySelector('[data-product-inquiry-form]');
      this.currentVariant = this.getSelectedVariant();

      this.openButton?.addEventListener('click', () => this.open());
      this.closeButton?.addEventListener('click', () => this.close());
      this.panel?.addEventListener('transitionend', (event) => this.handleTransitionEnd(event));
      this.form?.addEventListener('submit', (event) => this.handleSubmit(event));

      this.form?.querySelectorAll('[data-inquiry-field]').forEach((field) => {
        field.addEventListener('blur', () => this.validateField(field));
        field.addEventListener('input', () => this.clearFieldError(field));
      });

      if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (event.data?.sectionId !== this.dataset.sectionId) return;
            this.currentVariant = event.data.variant || this.getSelectedVariant();
          }
        );
      }

      this.handleVariantChanged = () => {
        this.currentVariant = this.getSelectedVariant();
      };

      document.addEventListener('variant:changed', this.handleVariantChanged);
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber?.();
      document.removeEventListener('variant:changed', this.handleVariantChanged);
    }

    getProductInfo() {
      return (
        this.closest('product-info') ||
        document.querySelector(`product-info[data-product-id="${this.dataset.productId}"]`)
      );
    }

    getSelectedVariant() {
      const productInfo = this.getProductInfo();
      if (!productInfo) return null;

      const selectedVariantJson = productInfo.querySelector(
        'variant-selects script[data-selected-variant]'
      )?.textContent;

      if (selectedVariantJson) {
        try {
          return JSON.parse(selectedVariantJson.trim());
        } catch (error) {
          console.error(error);
        }
      }

      const variantId = productInfo.querySelector(
        '.product-variant-id, form[is="product-form"] input[name="id"]'
      )?.value;

      if (!variantId) return null;

      return {
        id: Number(variantId),
        title: '',
        sku: '',
        options: [],
      };
    }

    getVariantOptionsLabel(variant) {
      if (!variant) return '';

      if (Array.isArray(variant.options) && variant.options.length) {
        return variant.options.filter(Boolean).join(' / ');
      }

      return [variant.option1, variant.option2, variant.option3].filter(Boolean).join(' / ');
    }

    getVariantPayload() {
      const variant = this.getSelectedVariant() || this.currentVariant;
      if (!variant) return {};

      const optionsLabel = this.getVariantOptionsLabel(variant);

      return {
        variant_id: variant.id,
        variant_title: variant.title || optionsLabel || '',
        variant_sku: variant.sku || '',
        variant_options: optionsLabel,
        variant_available: Boolean(variant.available),
      };
    }

    getProductUrlWithVariant(baseProductUrl, variant) {
      if (!baseProductUrl || !variant?.id) return baseProductUrl;

      const url = new URL(baseProductUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      return url.toString();
    }

    getHeaderOffset() {
      const headerHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        10
      );

      return (Number.isNaN(headerHeight) ? 80 : headerHeight) + ProductInquiryAccordion.SCROLL_OFFSET;
    }

    needsScrollIntoView() {
      const rect = this.getBoundingClientRect();
      const offset = this.getHeaderOffset();
      const bottomPadding = ProductInquiryAccordion.SCROLL_OFFSET;

      return rect.top < offset || rect.bottom > window.innerHeight - bottomPadding;
    }

    scrollIntoViewIfNeeded() {
      if (!this.needsScrollIntoView()) return;

      const rect = this.getBoundingClientRect();
      const targetTop = window.scrollY + rect.top - this.getHeaderOffset();

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      });
    }

    setAnimating(isAnimating) {
      this.classList.toggle('is-animating', isAnimating);

      if (isAnimating) {
        window.setTimeout(() => {
          this.classList.remove('is-animating');
        }, ProductInquiryAccordion.ANIMATION_MS + 50);
      }
    }

    open() {
      if (this.classList.contains('is-open') || this.classList.contains('is-animating')) return;

      this.scrollIntoViewIfNeeded();
      this.setAnimating(true);
      this.classList.add('is-open');
      this.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.classList.contains('is-open') || this.classList.contains('is-animating')) return;

      this.setAnimating(true);
      this.classList.remove('is-open');
      this.setAttribute('aria-expanded', 'false');
      this.resetFormState();
    }

    handleTransitionEnd(event) {
      if (event.propertyName !== 'grid-template-rows') return;
      if (event.target !== this.panel && event.target !== this.querySelector('.product-inquiry-accordion__toggle-wrap')) {
        return;
      }

      this.classList.remove('is-animating');
    }

    getFieldWrap(field) {
      return field.closest('[data-inquiry-field-wrap]');
    }

    getErrorElement(field) {
      const wrap = this.getFieldWrap(field);
      let error = wrap?.querySelector('.product-inquiry-form__error');

      if (!error && wrap) {
        error = document.createElement('span');
        error.className = 'product-inquiry-form__error';
        error.setAttribute('role', 'alert');
        wrap.appendChild(error);
      }

      return error;
    }

    setFieldError(field, message) {
      const error = this.getErrorElement(field);

      field.classList.add('input-error');
      field.setAttribute('aria-invalid', 'true');

      if (error) {
        error.textContent = message;
        field.setAttribute('aria-describedby', error.id || '');
        if (!error.id) {
          error.id = `${field.id}-error`;
          field.setAttribute('aria-describedby', error.id);
        }
      }
    }

    clearFieldError(field) {
      const wrap = this.getFieldWrap(field);
      const error = wrap?.querySelector('.product-inquiry-form__error');

      field.classList.remove('input-error');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');

      if (error) {
        error.textContent = '';
      }
    }

    validateField(field) {
      const key = field.dataset.inquiryField;
      const rules = ProductInquiryAccordion.VALIDATORS[key];

      if (!rules) return true;

      const value = field.value.trim();

      if (!value) {
        this.setFieldError(field, rules.empty);
        return false;
      }

      if (!rules.test(value)) {
        this.setFieldError(field, rules.invalid);
        return false;
      }

      this.clearFieldError(field);
      return true;
    }

    validateForm() {
      const fields = this.form?.querySelectorAll('[data-inquiry-field]') || [];
      let isValid = true;
      let firstInvalidField = null;

      fields.forEach((field) => {
        const fieldIsValid = this.validateField(field);

        if (!fieldIsValid) {
          isValid = false;

          if (!firstInvalidField) {
            firstInvalidField = field;
          }
        }
      });

      if (firstInvalidField) {
        firstInvalidField.focus({ preventScroll: true });
        this.scrollIntoViewIfNeeded();
      }

      return isValid;
    }

    getFieldValue(key) {
      return this.form?.querySelector(`[data-inquiry-field="${key}"]`)?.value.trim() || '';
    }

    clearAllErrors() {
      this.form?.querySelectorAll('[data-inquiry-field]').forEach((field) => {
        this.clearFieldError(field);
      });
    }

    setFeedbackState({ success = false, error = false }) {
      const fields = this.form?.querySelector('[data-product-inquiry-fields]');
      const successMessage = this.form?.querySelector('[data-product-inquiry-success]');
      const errorMessage = this.form?.querySelector('[data-product-inquiry-error]');

      fields?.classList.toggle('hidden', success);
      this.form?.classList.toggle('is-success', success);
      successMessage?.classList.toggle('hidden', !success);
      errorMessage?.classList.toggle('hidden', !error);
    }

    resetFormState() {
      this.setFeedbackState({ success: false, error: false });
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (!this.validateForm()) return;

      const submitButton = this.form?.querySelector('[data-product-inquiry-submit]');
      const firstName = this.getFieldValue('firstName');
      const lastName = this.getFieldValue('lastName');
      const phone = this.getFieldValue('phone');
      const email = this.getFieldValue('email');
      const message = this.getFieldValue('message');
      const productTitle = this.form?.querySelector('[name="product"]')?.value || '';
      const baseProductUrl = this.form?.querySelector('[name="product_url"]')?.value || '';
      const variant = this.getSelectedVariant() || this.currentVariant;
      const variantPayload = this.getVariantPayload();
      const productUrl = this.getProductUrlWithVariant(baseProductUrl, variant);

      this.setFeedbackState({ success: false, error: false });
      submitButton?.setAttribute('disabled', 'disabled');
      submitButton?.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(ProductInquiryAccordion.KLAVIYO_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-02-15',
          },
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                properties: {
                  first_name: firstName,
                  last_name: lastName,
                  email: email,
                  phone_number: phone,
                  message: message,
                  product_title: productTitle,
                  product_url: productUrl,
                  ...variantPayload,
                  request_type: 'product_inquiry',
                  submitted_at: new Date().toISOString(),
                  page_url: window.location.href,
                },
                metric: {
                  data: {
                    type: 'metric',
                    attributes: {
                      name: 'Product Inquiry Requested',
                    },
                  },
                },
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email,
                      first_name: firstName,
                      last_name: lastName,
                      phone_number: phone,
                    },
                  },
                },
              },
            },
          }),
        });

        if (response.ok) {
          this.setFeedbackState({ success: true, error: false });
          this.form?.reset();
          this.clearAllErrors();
        } else {
          console.error(await response.text());
          this.setFeedbackState({ success: false, error: true });
        }
      } catch (error) {
        console.error(error);
        this.setFeedbackState({ success: false, error: true });
      } finally {
        submitButton?.removeAttribute('disabled');
        submitButton?.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('product-inquiry-accordion', ProductInquiryAccordion);
}
